import type {
  ApiKeyInfo,
  ApiResponse,
  AuthResponse,
  AuthTokens,
  CatalogEntry,
  DataSource,
  CatalogSearchResult,
  CheckoutSession,
  CreateWatchlistItem,
  CreatedApiKey,
  Dashboard,
  ExportFormat,
  NormalizedSeries,
  PaidPlanId,
  UpsertDashboard,
  UsageSummary,
  User,
  WatchlistItem,
} from '@esgdash/shared';

const BASE_URL = import.meta.env.VITE_API_URL || '/api';

const ACCESS_KEY = 'esg_access_token';
const REFRESH_KEY = 'esg_refresh_token';

let accessToken: string | null = localStorage.getItem(ACCESS_KEY);
let refreshToken: string | null = localStorage.getItem(REFRESH_KEY);

export function setTokens(tokens: AuthTokens | null): void {
  if (tokens) {
    accessToken = tokens.accessToken;
    refreshToken = tokens.refreshToken;
    localStorage.setItem(ACCESS_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
  } else {
    accessToken = null;
    refreshToken = null;
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  }
}

export function hasSession(): boolean {
  return !!accessToken;
}

export class ApiClientError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export interface Envelope<T> {
  data: T;
  cached?: boolean;
  demo?: boolean;
}

let refreshing: Promise<boolean> | null = null;

async function attemptRefresh(): Promise<boolean> {
  if (!refreshToken) return false;
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch(`${BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        const body = (await res.json()) as ApiResponse<AuthResponse>;
        if (res.ok && body.success && body.data) {
          setTokens(body.data.tokens);
          return true;
        }
        setTokens(null);
        return false;
      } catch {
        return false;
      } finally {
        // Reset after the in-flight refresh settles.
        setTimeout(() => (refreshing = null), 0);
      }
    })();
  }
  return refreshing;
}

async function requestEnvelope<T>(
  path: string,
  init: RequestInit = {},
  allowRetry = true
): Promise<Envelope<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(`${BASE_URL}${path}`, { ...init, headers });

  let body: ApiResponse<T>;
  try {
    body = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiClientError(`Unexpected response (${res.status})`, res.status);
  }

  if (!res.ok || !body.success) {
    if (res.status === 401 && allowRetry && refreshToken) {
      const ok = await attemptRefresh();
      if (ok) return requestEnvelope<T>(path, init, false);
    }
    throw new ApiClientError(body.error ?? res.statusText, res.status, body.code);
  }

  return { data: body.data as T, cached: body.cached, demo: body.demo };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  return (await requestEnvelope<T>(path, init)).data;
}

export interface SeriesQuery {
  source: DataSource;
  code: string;
  country?: string;
  start?: string;
  end?: string;
}

export interface HealthInfo {
  status: string;
  version: string;
  environment: string;
  demo: boolean;
  features: { persistence: boolean; auth: boolean; cache: boolean; billing: boolean; email: boolean };
}

export interface ExportResult {
  blob: Blob;
  filename: string;
}

/** Trigger a browser download for an exported blob. */
export function triggerDownload({ blob, filename }: ExportResult): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const api = {
  health: () => request<HealthInfo>('/health'),

  auth: {
    register: (email: string, password: string, name: string) =>
      request<AuthResponse>('/auth/register', { method: 'POST', body: JSON.stringify({ email, password, name }) }),
    login: (email: string, password: string) =>
      request<AuthResponse>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
    me: () => request<User>('/auth/me'),
    logout: () =>
      request<{ ok: boolean }>('/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken }) }).catch(
        () => ({ ok: true })
      ),
    requestVerification: () =>
      request<{ ok: boolean; devLink?: string; alreadyVerified?: boolean; delivered?: 'provider' | 'logged' }>(
        '/auth/verify/request',
        { method: 'POST' }
      ),
    confirmVerification: (token: string) =>
      request<{ ok: boolean }>('/auth/verify/confirm', { method: 'POST', body: JSON.stringify({ token }) }),
    forgotPassword: (email: string) =>
      request<{ ok: boolean; devLink?: string }>('/auth/password/forgot', { method: 'POST', body: JSON.stringify({ email }) }),
    resetPassword: (token: string, password: string) =>
      request<{ ok: boolean }>('/auth/password/reset', { method: 'POST', body: JSON.stringify({ token, password }) }),
  },

  catalog: {
    list: (params: { category?: string; source?: string; esg?: boolean } = {}) => {
      const qs = new URLSearchParams();
      if (params.category) qs.set('category', params.category);
      if (params.source) qs.set('source', params.source);
      if (params.esg) qs.set('esg', 'true');
      return request<{ total: number; results: CatalogEntry[]; countries: { code: string; name: string }[] }>(
        `/catalog?${qs.toString()}`
      );
    },
    search: (q: string) => request<CatalogSearchResult>(`/catalog/search?q=${encodeURIComponent(q)}`),
  },

  data: {
    series: (q: SeriesQuery) => {
      const qs = new URLSearchParams({ source: q.source, code: q.code });
      if (q.country) qs.set('country', q.country);
      if (q.start) qs.set('start', q.start);
      if (q.end) qs.set('end', q.end);
      return requestEnvelope<NormalizedSeries>(`/data/series?${qs.toString()}`);
    },
    export: async (q: SeriesQuery, format: ExportFormat, allowRetry = true): Promise<ExportResult> => {
      const qs = new URLSearchParams({ source: q.source, code: q.code, format });
      if (q.country) qs.set('country', q.country);
      const headers: Record<string, string> = {};
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

      const res = await fetch(`${BASE_URL}/data/series/export?${qs.toString()}`, { headers });
      if (!res.ok) {
        if (res.status === 401 && allowRetry && refreshToken && (await attemptRefresh())) {
          return api.data.export(q, format, false);
        }
        let message = res.statusText;
        let code: string | undefined;
        try {
          const body = (await res.json()) as ApiResponse<never>;
          message = body.error ?? message;
          code = body.code;
        } catch {
          /* non-JSON error */
        }
        throw new ApiClientError(message, res.status, code);
      }
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') ?? '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match?.[1] ?? `${q.source}_${q.code}.${format}`;
      return { blob, filename };
    },
  },

  billing: {
    checkout: (plan: PaidPlanId) =>
      request<CheckoutSession>('/billing/checkout', { method: 'POST', body: JSON.stringify({ plan }) }),
    portal: () => request<CheckoutSession>('/billing/portal', { method: 'POST' }),
  },

  watchlist: {
    list: () => request<WatchlistItem[]>('/watchlist'),
    add: (item: CreateWatchlistItem) => request<WatchlistItem>('/watchlist', { method: 'POST', body: JSON.stringify(item) }),
    remove: (id: string) => request<{ ok: boolean }>(`/watchlist/${id}`, { method: 'DELETE' }),
  },

  dashboards: {
    list: () => request<Dashboard[]>('/dashboards'),
    get: (id: string) => request<Dashboard>(`/dashboards/${id}`),
    create: (input: UpsertDashboard) => request<Dashboard>('/dashboards', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: UpsertDashboard) =>
      request<Dashboard>(`/dashboards/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
    remove: (id: string) => request<{ ok: boolean }>(`/dashboards/${id}`, { method: 'DELETE' }),
  },

  keys: {
    list: () => request<ApiKeyInfo[]>('/keys'),
    create: (name: string) => request<CreatedApiKey>('/keys', { method: 'POST', body: JSON.stringify({ name }) }),
    remove: (id: string) => request<{ ok: boolean }>(`/keys/${id}`, { method: 'DELETE' }),
  },

  usage: () => request<UsageSummary>('/usage'),
};
